// ============================================================================
// DESTINATION:  src/features/auth/hooks/useSessionQuery.ts
// ACTION:       NEW FILE (delete old useSession.ts)
// ============================================================================

/**
 * ============================================================================
 * FILE: src/features/auth/hooks/useSessionQuery.ts
 * (this is your old useSession.ts — RENAMED, and the Redux sync turned on)
 * ============================================================================
 *
 * ⚠️ RENAME, DO NOT keep both. Delete src/features/auth/hooks/useSession.ts.
 * The reason for the rename: there is no SessionContext in this design, so the
 * name `useSession` is free — but keeping a second export called `useSession`
 * anywhere invites the auto-import-picks-the-wrong-one bug. One name, one hook.
 *
 * ============================================================================
 * WHAT THIS DOES
 * ============================================================================
 * This is the 15-second poll — the GUARANTEE behind single-session and promotion
 * expiry. Broadcasts are faster but lossy (a backgrounded tab, a private-mode
 * localStorage throw); the poll is what makes the guarantees actually hold.
 *
 * On every response it SYNCS REDUX — which your original had commented out
 * ("Sync with Redux on success"). That comment was the bug: without it, a fresh
 * page load never dispatches setUser, and any guard gating on
 * `auth.isAuthenticating` (RequirePaid, RequireUnpaid) spins forever because the
 * flag starts true and nothing flips it.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { authApi } from "../api/auth.api";
import { AUTH_QUERY_KEYS } from "../types/auth.types";
import type { ISessionStatusResponse } from "../types/auth.types";
import { useAppDispatch } from "@/store/hooks";
import {
  setSessionVersion,
  setSessionType,
  updateUserRole,
  clearUser,
} from "@/store/slices/auth";

/**
 * How often the poll runs. 15s is the promise: "an admin force-logout, a
 * second-device login, or a promo expiry takes effect within 15 seconds".
 * Shorter costs requests; longer widens the window a revoked session stays live.
 */
const SESSION_POLL_MS = 15_000;

/**
 * useSessionQuery — the session heartbeat.
 *
 * Mount it ONCE, high in the tree (SessionWatcher does this). Mounting it in
 * multiple places would run multiple polls; React Query dedupes by key, so it is
 * safe, but there is no reason to.
 */
export const useSessionQuery = () => {
  const dispatch = useAppDispatch();

  const query = useQuery<ISessionStatusResponse, Error>({
    queryKey: AUTH_QUERY_KEYS.session,
    queryFn: () => authApi.sessionStatus(),

    // The poll.
    refetchInterval: SESSION_POLL_MS,
    // Keep polling even when the tab is backgrounded — a promo can expire while
    // the user is in another tab, and they should come back to the right price.
    refetchIntervalInBackground: true,

    // A 401 here is handled by the interceptor (it dispatches sessionInvalidated
    // and navigates). So the query itself must NOT retry it — queryClient's retry
    // policy already refuses 401/403, this is belt-and-braces.
    retry: false,

    // Fresh enough that a manual refetch elsewhere does not double-hit.
    staleTime: SESSION_POLL_MS,
  });

  /**
   * SYNC → REDUX. This is the part your original commented out.
   *
   * We do it in an effect keyed on the data (not in queryFn) so it re-runs each
   * time the poll returns a NEW value, and so React Query stays the fetch owner
   * while Redux stays the app-state owner.
   *
   * We do NOT dispatch setUser here — the poll response is session status, not a
   * full user object. verifyToken owns setUser. The poll owns the VERSION.
   *
   * updateUserRole is the exception: it's a narrow patch (see authSlice.ts),
   * not a replace, and role rides along in this same response for free. This
   * is the FALLBACK path for noticing "paid in another tab" — the fast path
   * is ProtectedRoutes' focus-triggered /check-session effect; this poll
   * catches it within 15s even if this tab is never refocused.
   */
  useEffect(() => {
    if (!query.data) return;

    if (query.data.valid) {
      dispatch(setSessionVersion(query.data.sessionVersion));
      dispatch(setSessionType(query.data.sessionType));
      if (query.data.role) dispatch(updateUserRole(query.data.role));
    } else {
      // Server says this session is no longer valid, but returned 200 (not 401).
      // Treat it as a logout. The interceptor covers the 401 path; this covers a
      // backend that answers `{ valid: false }` with a 200.
      dispatch(clearUser());
    }
  }, [query.data, dispatch]);

  return query;
};

/**
 * useCurrentUser — unchanged from yours. Kept for anything that reads the user
 * via React Query rather than Redux.
 */
export const useCurrentUser = () => {
  return useQuery({
    queryKey: AUTH_QUERY_KEYS.me,
    queryFn: () => authApi.me(),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
};
